import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Clock, CheckCircle, Users, BookOpen, Info } from 'lucide-react';
import api from '../utils/api';

// Hook to detect mobile viewport and force re-renders
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};

interface Subject {
  id: string;
  name: string;
  category: 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY';
  onlineRate: number;
  inPersonRate: number;
}

interface TimeSlot {
  id: string;
  startTime: string;
  endTime: string;
  tutor: {
    id: string;
    name: string;
  };
}

const ConsultationPage: React.FC = () => {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchSubjects();
  }, []);

  useEffect(() => {
    if (selectedDate && selectedSubject) {
      fetchTimeSlots();
    } else {
      setTimeSlots([]); // Clear time slots when date or subject changes
    }
  }, [selectedDate, selectedSubject]);

  // Force re-render on mobile and handle layout issues
  useEffect(() => {
    if (timeSlots.length > 0) {
      if (isMobile) {
        // Multiple strategies to fix mobile rendering
        const timer = setTimeout(() => {
          // Strategy 1: Force container visibility
          const container = document.querySelector('[data-consultation-slots]') as HTMLElement;
          if (container) {
            container.style.opacity = '0';
            container.style.visibility = 'hidden';
            void container.offsetHeight; // Trigger reflow
            container.style.opacity = '1';
            container.style.visibility = 'visible';
          }
          
          // Strategy 2: Force parent layout recalculation
          const mainContent = document.querySelector('main') as HTMLElement;
          if (mainContent) {
            mainContent.style.transform = 'translateZ(0)';
            void mainContent.offsetHeight;
            mainContent.style.transform = '';
          }
        }, 100);
        
        return () => clearTimeout(timer);
      }
    }
  }, [timeSlots, isMobile]);

  const fetchSubjects = async () => {
    try {
      const response = await api.get('/subjects');
      // Show every active level, including UNIVERSITY (Oxbridge), so
      // university applicants can book a consultation too.
      const consultationSubjects = response.data.filter((subject: Subject) =>
        ['ELEVEN_PLUS', 'GCSE', 'A_LEVEL', 'UNIVERSITY'].includes(subject.category)
      );
      setSubjects(consultationSubjects);
    } catch (error) {
      console.error('Failed to fetch subjects:', error);
      setError('Failed to load subjects. Please try again.');
    }
  };

  const fetchTimeSlots = async () => {
    if (!selectedDate || !selectedSubject) return;
    
    setLoading(true);
    setError('');
    
    const dateParts = selectedDate.split('-').map(Number);
    const dateFromParts = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]); // Local date

    try {
      // Use the most reliable date parsing method (parts-based)
      const reliableDate = dateFromParts.toISOString().split('T')[0];
      const apiUrl = `/availability/subject/${selectedSubject}?date=${reliableDate}`;

      // Use the same subject availability endpoint as regular bookings
      const response = await api.get(apiUrl);
      setTimeSlots(response.data);
    } catch (error: any) {
      console.error('ConsultationPage - Failed to fetch time slots:', error);
      setError('Failed to load available time slots. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBooking = async (timeSlot: TimeSlot) => {
    if (!selectedSubject) {
      setError('Please select a subject before booking.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const bookingData = {
        tutorId: timeSlot.tutor.id,
        subjectId: selectedSubject,
        startTime: timeSlot.startTime,
        endTime: getConsultationEndTime(timeSlot.startTime),
        notes: 'Consultation booking'
      };

      await api.post('/bookings/consultation', bookingData);
      setSuccess("Consultation request sent. You haven't been charged — Ethan will confirm the booking and email you a £5 payment link.");
      
      // Refresh available slots
      fetchTimeSlots();
      
      // Clear form
      setSelectedSubject('');
      setSelectedDate('');
      setTimeSlots([]);
    } catch (error: any) {
      console.error('Failed to book consultation:', error);
      setError(error.response?.data?.message || 'Failed to book consultation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-GB', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true,
      timeZone: 'Europe/London'
    });
  };

  const getConsultationEndTime = (startTime: string) => {
    const start = new Date(startTime);
    const end = new Date(start.getTime() + 20 * 60 * 1000); // Add 20 minutes
    return end.toISOString();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'Europe/London'
    });
  };

  const formatSubjectCategory = (category: 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY') => {
    switch (category) {
      case 'ELEVEN_PLUS':
        return '11+';
      case 'GCSE':
        return 'GCSE';
      case 'A_LEVEL':
        return 'A-Level';
      case 'UNIVERSITY':
        return 'University';
      default:
        return category;
    }
  };

  const groupSubjectsByCategory = () => {
    const groups = {
      ELEVEN_PLUS: subjects.filter(s => s.category === 'ELEVEN_PLUS'),
      GCSE: subjects.filter(s => s.category === 'GCSE'),
      A_LEVEL: subjects.filter(s => s.category === 'A_LEVEL'),
      UNIVERSITY: subjects.filter(s => s.category === 'UNIVERSITY')
    };
    return groups;
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 6); // Allow booking up to 6 months in advance
    return maxDate.toISOString().split('T')[0];
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Book a Consultation</h1>
        <p className="text-gray-600 mb-4">
          Get personalized guidance with a 20-minute consultation for just £5
        </p>
        
        {/* Benefits Section */}
        <div className="bg-primary-50 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-primary-900 mb-3">What's included in your consultation:</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-primary-900">Personalized Assessment</h3>
                <p className="text-sm text-primary-700">Understand your current level and learning goals</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Users className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-primary-900">Custom Learning Plan</h3>
                <p className="text-sm text-primary-700">Get a tailored study approach for your needs</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <BookOpen className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-primary-900">Resource Recommendations</h3>
                <p className="text-sm text-primary-700">Discover the best materials for your level</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Clock className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-primary-900">Next Steps Guidance</h3>
                <p className="text-sm text-primary-700">Clear action plan for your learning journey</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-800">{success}</p>
        </div>
      )}

      {/* Booking Form */}
      <div className="card mb-6">
        <div className="card-header">
          <h2 className="card-title">Select Your Consultation Details</h2>
        </div>
        
        <div className="space-y-6">
          {/* Subject Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Choose Your Subject Area
            </label>
            <div className="space-y-4">
              {Object.entries(groupSubjectsByCategory()).map(([category, categorySubjects]) => {
                if (categorySubjects.length === 0) return null;
                
                const categoryName = formatSubjectCategory(category as 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY');
                
                return (
                  <div key={category}>
                    <h3 className="text-sm font-medium text-gray-900 mb-2">{categoryName} Subjects</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {categorySubjects.map((subject) => (
                        <label key={subject.id} className="relative">
                          <input
                            type="radio"
                            name="subject"
                            value={subject.id}
                            checked={selectedSubject === subject.id}
                            onChange={(e) => setSelectedSubject(e.target.value)}
                            className="peer sr-only"
                          />
                          <div className="cursor-pointer rounded-lg border border-gray-200 p-4 text-sm hover:bg-gray-50 peer-checked:bg-primary-50 peer-checked:border-primary-200 peer-checked:text-primary-900">
                            <div className="font-medium">{subject.name}</div>
                            <div className="text-xs text-gray-500 mt-1">{categoryName}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Date Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Date
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              min={getMinDate()}
              max={getMaxDate()}
              className="input max-w-xs"
            />
          </div>

          {/* Pricing Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900">Consultation Fee</h3>
                <p className="text-sm text-gray-600">20-minute session</p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-primary-600">£5</div>
                <p className="text-sm text-gray-500">One-time fee</p>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 text-blue-900">
            <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium">You won't be charged when you book.</p>
              <p className="mt-1 text-blue-800">
                Picking a slot below sends a booking request. Once Ethan confirms it, you'll get a payment link by email for the £5 consultation fee.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Available Time Slots */}
      {selectedDate && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Available Time Slots</h2>
            <p className="card-subtitle">
              {formatDate(selectedDate)} - 20-minute consultation sessions
            </p>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading available slots...</p>
            </div>
          ) : timeSlots.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No consultation slots available for this date.</p>
              <p className="text-sm text-gray-500 mt-1">Try selecting a different date.</p>
            </div>
          ) : (
            <div className="w-full">
              <div
                data-consultation-slots 
                className="w-full"
                style={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                  minHeight: '100px'
                }}
              >
              {timeSlots.map((slot) => (
                <div 
                  key={slot.id} 
                  className="border border-gray-200 rounded-lg p-4"
                  style={{
                    display: 'block',
                    visibility: 'visible',
                    opacity: 1,
                    minHeight: '80px',
                    width: '100%'
                  }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="font-medium">
                        {formatTime(slot.startTime)} - {formatTime(getConsultationEndTime(slot.startTime))}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    with {slot.tutor.name}
                  </p>
                  <button
                    onClick={() => handleBooking(slot)}
                    disabled={loading || !selectedSubject}
                    className="w-full btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Sending request...' : 'Request consultation — £5 after confirmation'}
                  </button>
                </div>
              ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ConsultationPage;